// src/components/customMaterial/CustomMaterialForm.js
//
// Request Custom Material — a three-step pop-up form.
//
//   1  About      topic, subject, grade, song style
//   2  Content    facts (one per line), quiz questions, matching pairs
//   3  Review     everything at a glance, then Pay $29 & send
//
// mode
//   "live"    a subscriber: step 3 opens Stripe Checkout (startCustomMaterialCheckout)
//   "locked"  signed in but on the free trial / free level: they can fill it
//             all in (the draft is kept), and step 3 says a subscription is
//             needed, with a link to the plans
//   "demo"    the public home page: filled with an example, everything is
//             clickable, nothing is saved, and sending says to sign up
//
// Styles: customMaterial.css (scoped to .cmf-).

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebaseConfig";
import { SUBJECTS } from "../../data/subjects";
import {
  CUSTOM_MATERIAL_PRICE_LABEL,
  SONG_STYLES,
  DEMO_REQUEST,
} from "../../config/customMaterial";
import "./customMaterial.css";

const DRAFT_KEY = "sbl-custom-material-draft";
const GRADES = [
  ["prek", "Pre-K"], ["k", "Kindergarten"], ["1st", "1st"], ["2nd", "2nd"], ["3rd", "3rd"], ["4th", "4th"],
  ["5th", "5th"], ["6th", "6th"], ["7th", "7th"], ["8th", "8th"], ["9th", "9th"], ["10th", "10th"],
  ["11th", "11th"], ["12th", "12th"],
];
const STEPS = ["About", "Content", "Review"];

const blankQuestion = () => ({ question: "", answer: "", wrong: ["", "", ""] });
const blankPair = () => ({ left: "", right: "" });

function emptyRequest(defaults = {}) {
  return {
    topic: "",
    subject: defaults.subject && defaults.subject !== "other" ? defaults.subject : "science",
    grade: defaults.grade || "",
    state: defaults.state || "",
    style: "Any Style",
    factsText: "",
    questions: [blankQuestion()],
    pairs: [],
    notes: "",
  };
}

const fromDemo = () => ({
  ...DEMO_REQUEST,
  factsText: DEMO_REQUEST.facts.join("\n"),
  questions: DEMO_REQUEST.questions.map((q) => ({ ...q, wrong: [...q.wrong] })),
  pairs: DEMO_REQUEST.pairs.map((p) => ({ ...p })),
});

const readDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeDraft = (r) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(r));
  } catch {
    /* private mode — nothing to keep */
  }
};
export const clearCustomDraft = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
};

/** the request as the server wants it */
function toPayload(r) {
  return {
    topic: r.topic.trim(),
    subject: r.subject,
    grade: r.grade,
    state: r.state,
    style: r.style,
    notes: r.notes.trim(),
    facts: r.factsText.split("\n").map((f) => f.trim()).filter(Boolean),
    questions: r.questions
      .map((q) => ({ question: q.question.trim(), answer: q.answer.trim(), wrong: q.wrong.map((w) => w.trim()).filter(Boolean) }))
      .filter((q) => q.question && q.answer),
    pairs: r.pairs.map((p) => ({ left: p.left.trim(), right: p.right.trim() })).filter((p) => p.left && p.right),
  };
}

function problemsFor(step, r) {
  const p = toPayload(r);
  if (step === 0) {
    if (!p.topic) return "Give your topic a name (for example “The Water Cycle”).";
    if (!p.grade) return "Pick a grade.";
  }
  if (step >= 1) {
    if (p.facts.length + p.questions.length < 3) return "Add at least 3 facts or questions so we have enough to write about.";
    const half = r.questions.find((q) => (q.question.trim() && !q.answer.trim()) || (!q.question.trim() && q.answer.trim()));
    if (half) return "Each question needs its right answer (or clear the question).";
  }
  return "";
}

export default function CustomMaterialForm({ open, onClose, mode = "live", defaults = {}, from = "", returnPath = "" }) {
  const navigate = useNavigate();
  const demo = mode === "demo";
  const [step, setStep] = useState(0);
  const [req, setReq] = useState(() => (demo ? fromDemo() : readDraft() || emptyRequest(defaults)));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(false); // the "you need a subscription" card

  // a fresh start each time it opens
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError("");
    setGate(false);
    setBusy(false);
    setReq(demo ? fromDemo() : readDraft() || emptyRequest(defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, demo]);

  // keep a signed-in teacher's work as they type
  useEffect(() => {
    if (open && !demo) writeDraft(req);
  }, [open, demo, req]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  const payload = useMemo(() => toPayload(req), [req]);
  if (!open) return null;

  const set = (patch) => setReq((r) => ({ ...r, ...patch }));
  const setQuestion = (i, patch) =>
    setReq((r) => ({ ...r, questions: r.questions.map((q, n) => (n === i ? { ...q, ...patch } : q)) }));
  const setWrong = (i, w, value) =>
    setReq((r) => ({
      ...r,
      questions: r.questions.map((q, n) => (n === i ? { ...q, wrong: q.wrong.map((x, k) => (k === w ? value : x)) } : q)),
    }));
  const setPair = (i, patch) => setReq((r) => ({ ...r, pairs: r.pairs.map((p, n) => (n === i ? { ...p, ...patch } : p)) }));

  const next = () => {
    const problem = problemsFor(step, req);
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const goTo = (n) => {
    if (n <= step) {
      setError("");
      setStep(n);
      return;
    }
    for (let k = step; k < n; k += 1) {
      const problem = problemsFor(k, req);
      if (problem) {
        setError(problem);
        setStep(k);
        return;
      }
    }
    setError("");
    setStep(n);
  };

  const send = async () => {
    const problem = problemsFor(1, req) || problemsFor(0, req);
    if (problem) {
      setError(problem);
      return;
    }
    if (mode !== "live") {
      setGate(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const start = httpsCallable(functions, "startCustomMaterialCheckout");
      const { data } = await start({
        request: payload,
        from,
        origin: window.location.origin,
        returnPath: returnPath || `${window.location.pathname}${window.location.search}`,
      });
      window.location.href = data.url;
    } catch (err) {
      setBusy(false);
      if (err?.code === "functions/permission-denied") {
        setGate(true);
        return;
      }
      setError(err?.message || "Something went wrong starting checkout. Please try again.");
    }
  };

  const subjectLabel = SUBJECTS.find((s) => s.value === req.subject)?.label || req.subject;
  const gradeLabel = GRADES.find(([v]) => v === req.grade)?.[1] || req.grade;

  const body = (
    <div
      className="cmf-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Request custom material"
      onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose?.()}
    >
      <div className="cmf-box">
        <header className="cmf-head">
          <div>
            <h2>✏️ Request Custom Material</h2>
            <p>Send us your facts — we turn them into a song in the style you choose, plus a game to help students remember exactly what they need.</p>
          </div>
          <button type="button" className="cmf-x" onClick={onClose} disabled={busy} aria-label="Close">
            ✕
          </button>
        </header>

        {demo && (
          <div className="cmf-banner is-demo">
            👀 <b>This is a preview.</b> Click around and see how it works — sending a request needs a Smart Board Learning account with a subscription.
          </div>
        )}
        {mode === "locked" && (
          <div className="cmf-banner is-locked">
            🔒 <b>Custom material is included with a subscription.</b> Fill this in now — we keep your draft — and send it once you subscribe.
          </div>
        )}

        <ol className="cmf-steps">
          {STEPS.map((label, n) => (
            <li key={label}>
              <button type="button" className={`cmf-step ${n === step ? "is-on" : ""} ${n < step ? "is-done" : ""}`} onClick={() => goTo(n)}>
                <span>{n < step ? "✓" : n + 1}</span>
                {label}
              </button>
            </li>
          ))}
        </ol>

        <div className="cmf-body">
          {step === 0 && (
            <div className="cmf-grid">
              <label className="cmf-field cmf-wide">
                Topic name
                <input value={req.topic} onChange={(e) => set({ topic: e.target.value })} placeholder="e.g. The Water Cycle" maxLength={120} />
              </label>
              <label className="cmf-field">
                Subject
                <select value={req.subject} onChange={(e) => set({ subject: e.target.value })}>
                  {SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.icon} {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="cmf-field">
                Grade
                <select value={req.grade} onChange={(e) => set({ grade: e.target.value })}>
                  <option value="">Choose…</option>
                  {GRADES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <div className="cmf-field cmf-wide">
                Song style
                <div className="cmf-chips">
                  {SONG_STYLES.map((st) => (
                    <button key={st} type="button" className={`cmf-chip ${req.style === st ? "is-on" : ""}`} onClick={() => set({ style: st })}>
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="cmf-content">
              <label className="cmf-field">
                Facts for the song <small>one per line</small>
                <textarea
                  rows={6}
                  value={req.factsText}
                  onChange={(e) => set({ factsText: e.target.value })}
                  placeholder={"The sun heats water and it turns into vapor.\nWater vapor cools into clouds."}
                />
              </label>

              <div className="cmf-section">
                <div className="cmf-section-head">
                  <h3>Quiz questions</h3>
                  <small>The right answer, plus up to 3 wrong ones (optional — we can write those).</small>
                </div>
                {req.questions.map((q, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="cmf-q">
                    <div className="cmf-q-top">
                      <b>{i + 1}</b>
                      <input value={q.question} onChange={(e) => setQuestion(i, { question: e.target.value })} placeholder="Question" maxLength={300} />
                      <button
                        type="button"
                        className="cmf-remove"
                        onClick={() => setReq((r) => ({ ...r, questions: r.questions.filter((_, n) => n !== i) }))}
                        aria-label={`Remove question ${i + 1}`}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="cmf-q-answers">
                      <input className="is-right" value={q.answer} onChange={(e) => setQuestion(i, { answer: e.target.value })} placeholder="✓ Right answer" maxLength={160} />
                      {q.wrong.map((w, k) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <input key={k} value={w} onChange={(e) => setWrong(i, k, e.target.value)} placeholder="Wrong answer" maxLength={160} />
                      ))}
                    </div>
                  </div>
                ))}
                {req.questions.length < 25 && (
                  <button type="button" className="cmf-add" onClick={() => setReq((r) => ({ ...r, questions: [...r.questions, blankQuestion()] }))}>
                    + Add a question
                  </button>
                )}
              </div>

              <div className="cmf-section">
                <div className="cmf-section-head">
                  <h3>Matching pairs</h3>
                  <small>Optional — for a drag-and-drop game (a word and what it matches).</small>
                </div>
                {req.pairs.map((p, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="cmf-pair">
                    <input value={p.left} onChange={(e) => setPair(i, { left: e.target.value })} placeholder="Word" maxLength={120} />
                    <span aria-hidden="true">↔</span>
                    <input value={p.right} onChange={(e) => setPair(i, { right: e.target.value })} placeholder="Matches with" maxLength={160} />
                    <button
                      type="button"
                      className="cmf-remove"
                      onClick={() => setReq((r) => ({ ...r, pairs: r.pairs.filter((_, n) => n !== i) }))}
                      aria-label={`Remove pair ${i + 1}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {req.pairs.length < 20 && (
                  <button type="button" className="cmf-add" onClick={() => setReq((r) => ({ ...r, pairs: [...r.pairs, blankPair()] }))}>
                    + Add a pair
                  </button>
                )}
              </div>

              <label className="cmf-field">
                Anything else? <small>optional</small>
                <textarea rows={2} value={req.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Words to use or avoid, your students' names for a shout-out…" maxLength={1200} />
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="cmf-review">
              <div className="cmf-sum">
                <div><span>Topic</span><b>{payload.topic}</b></div>
                <div><span>Subject</span><b>{subjectLabel}</b></div>
                <div><span>Grade</span><b>{gradeLabel}</b></div>
                <div><span>Song style</span><b>{req.style}</b></div>
              </div>
              <div className="cmf-sum-lists">
                <div>
                  <h4>{payload.facts.length} fact{payload.facts.length === 1 ? "" : "s"}</h4>
                  <ul>{payload.facts.slice(0, 6).map((f) => <li key={f}>{f}</li>)}</ul>
                  {payload.facts.length > 6 && <small>+ {payload.facts.length - 6} more</small>}
                </div>
                <div>
                  <h4>{payload.questions.length} question{payload.questions.length === 1 ? "" : "s"}</h4>
                  <ul>
                    {payload.questions.slice(0, 5).map((q) => (
                      <li key={q.question}>
                        {q.question} <em>✓ {q.answer}</em>
                      </li>
                    ))}
                  </ul>
                  {payload.pairs.length > 0 && <small>{payload.pairs.length} matching pair{payload.pairs.length === 1 ? "" : "s"}</small>}
                </div>
              </div>
              <div className="cmf-price">
                <div>
                  <b>{CUSTOM_MATERIAL_PRICE_LABEL}</b> one request
                  <small>A custom song in your style + a matching game for this topic.</small>
                </div>
              </div>
              <p className="cmf-fine">
                Please allow 7–14 days for the material to be added to your portal. Payment is by card through Stripe. By paying you agree to our <a href="/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>.
              </p>
            </div>
          )}

          {error && <p className="cmf-error">{error}</p>}
        </div>

        <footer className="cmf-foot">
          {step > 0 ? (
            <button type="button" className="cmf-btn is-ghost" onClick={() => { setError(""); setStep((s) => s - 1); }} disabled={busy}>
              ← Back
            </button>
          ) : (
            <span />
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" className="cmf-btn" onClick={next}>
              Next →
            </button>
          ) : (
            <button type="button" className="cmf-btn is-pay" onClick={send} disabled={busy}>
              {busy ? "Opening checkout…" : mode === "live" ? `Pay ${CUSTOM_MATERIAL_PRICE_LABEL} & send` : `Send request · ${CUSTOM_MATERIAL_PRICE_LABEL}`}
            </button>
          )}
        </footer>

        {gate && (
          <div className="cmf-gate" role="alertdialog" aria-label="Subscription needed">
            <div className="cmf-gate-card">
              <div className="cmf-gate-icon" aria-hidden="true">🔒</div>
              {demo ? (
                <>
                  <h3>Sign up to request custom material</h3>
                  <p>
                    Custom songs and games are a subscriber feature. Start with a free 60-day trial to try everything else, then subscribe to send requests like this one.
                  </p>
                  <div className="cmf-gate-actions">
                    <button type="button" className="cmf-btn is-pay" onClick={() => navigate("/register")}>Start free trial</button>
                    <button type="button" className="cmf-btn is-ghost" onClick={() => setGate(false)}>Keep looking</button>
                  </div>
                </>
              ) : (
                <>
                  <h3>A subscription is needed for this one</h3>
                  <p>
                    Custom material requests are for subscribers (the free trial includes everything else). Choose a plan and come back — your request is saved on this device, ready to send.
                  </p>
                  <div className="cmf-gate-actions">
                    <button type="button" className="cmf-btn is-pay" onClick={() => navigate("/choose-plan")}>See plans</button>
                    <button type="button" className="cmf-btn is-ghost" onClick={() => setGate(false)}>Back to my request</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
